import React, { useCallback, useContext, useEffect, useRef, useState } from 'react'
import { Container, Title, TitleWrapper } from '../../../components/listing/AMSListing'
import { TitleGroup } from '../../../components/listing/AMSDynamicListing'
import StyledText from '../../../components/StyledComponents/StyledText'
import { useTheme } from '../../../middleware/ThemeContext'
import { Tabs } from '../../../components/Tabs/Tabs'
import Setting from './Setting'
import Pages from './Pages'
import MenuBuilder from './MenuComponents/MenuBuilder'
import SeoSetting from './SeoSetting'
import DomainSettings from './DomainSettings'
import { Button } from '../../../components/form/Button'
import { WebsiteFormValues } from '../../../types/types'
import { useCreateMenuMutation, useGetSettingDetailsQuery, useUpdateSettingDetailsMutation } from '../../../api/pageBuilderApiSlice'
import { useToast } from '../../../components/toast/ToastContext'
import ConfirmationPrompt from '../../../components/StyledComponents/ConfirmationPrompt'
import { UNSAFE_NavigationContext as NavigationContext } from 'react-router-dom'

export interface MenuBuilderRef {
    handleSubmit: () => void;
    handleCancel: () => void;
    hasUnsavedChanges: () => boolean;
}
const Builder = () => {
    const TABS_STORAGE_KEY = "activeTabId";
    const { theme } = useTheme();
    const showToast = useToast();
    const tabsContainerRef = useRef<HTMLDivElement>(null);
    const submitRef = useRef<MenuBuilderRef | null>(null);
    const pendingTabIdRef = useRef<number | null>(null);
    const pendingNavigationActionRef = useRef<(() => void) | null>(null);
    const initialSettingSnapshotRef = useRef('');
    const initialSeoSnapshotRef = useRef('');
    const initialSeoStateRef = useRef<{ metaTags: string[]; description: string; newMetaTag: string }>({
        metaTags: [],
        description: '',
        newMetaTag: '',
    });
    const navigationConfirmedRef = useRef(false);
    const [isResponsive, setIsResponsive] = useState(false);
    const [showWarningPopup, setShowWarningPopup] = useState(false);
    const [pagesRefreshToken, setPagesRefreshToken] = useState(0);
    const [menuRefreshToken, setMenuRefreshToken] = useState(0);
    const [updateSettingDetails, { isLoading: isUpdating }] = useUpdateSettingDetailsMutation();
    const [createMenu, { isLoading: isCreating }] = useCreateMenuMutation();

    const { data: company, refetch, isLoading: companyLoading } = useGetSettingDetailsQuery(undefined, {
        refetchOnMountOrArgChange: true,
    });

    const [metaTags, setMetaTags] = useState<string[]>([]);
    const [description, setDescription] = useState<string>('');
    const [newMetaTag, setNewMetaTag] = useState<string>('');
    const [settingConfiguration, setSettingConfiguration] = useState<WebsiteFormValues>({
        websiteName: "",
        websiteURl: "",
        logo: "",
        primaryColor: '#0050FF',
        secondaryColor: '#F59E0B',
        textColor: '#003366',
        fontFamily: "Roboto",
        logoPreview: "",
        timezone: "",
        loginDescription: "",
        loginHeading: "",
        favicon: "",
        faviconPreview: "",
        termOfService: "",
        privacyPolicy: "",
        cookieEnabled: false,
        headerHtml: "",
        customCss: "",
        customJs: "",
        fileName: "",
        fileSize: 0,
        logoFileName: "",
        logoFileSize: 0,
        faviconFileName: "",
        faviconFileSize: 0,
        statusId: company?.website?.statusId ?? "",
    });

    const [tabs, setTabs] = React.useState(() => {
        const savedTabId = localStorage.getItem(TABS_STORAGE_KEY);
        const activeId = savedTabId ? Number(savedTabId) : 1; // default 1 (Settings)

        return [
            { id: 1, title: "Settings", active: activeId === 1 },
            { id: 2, title: "Pages", active: activeId === 2 },
            { id: 3, title: "Menu Management", active: activeId === 3 },
            { id: 4, title: "SEO Settings", active: activeId === 4 },
            { id: 5, title: "Domain Settings", active: activeId === 5 },
        ];
    });

    const serializeState = useCallback((value: unknown) => JSON.stringify(value ?? null), []);

    const getActiveTabId = useCallback(() => tabs.find((tab) => tab.active)?.id ?? 1, [tabs]);

    const applyTabChange = useCallback((id: number) => {
        const activeTabId = getActiveTabId();

        if (activeTabId === 4 && id !== 4) {
            setMetaTags(initialSeoStateRef.current.metaTags);
            setDescription(initialSeoStateRef.current.description);
            setNewMetaTag(initialSeoStateRef.current.newMetaTag);
        }

        setTabs(prev =>
            prev.map(tab => ({
                ...tab,
                active: tab.id === id,
            }))
        );
        localStorage.setItem(TABS_STORAGE_KEY, id.toString()); // save active tab
    }, [getActiveTabId]);

    const hasDirtySettings = useCallback(() => {
        return serializeState(settingConfiguration) !== initialSettingSnapshotRef.current;
    }, [serializeState, settingConfiguration]);

    const hasDirtySeo = useCallback(() => {
        return serializeState({ metaTags, description }) !== initialSeoSnapshotRef.current;
    }, [serializeState, metaTags, description]);

    const hasDirtyMenu = useCallback(() => {
        return submitRef.current?.hasUnsavedChanges?.() ?? false;
    }, []);

    const hasUnsavedChanges = useCallback(() => {
        const activeTabId = getActiveTabId();

        if (activeTabId === 1) {
            return hasDirtySettings();
        }

        if (activeTabId === 3) {
            return hasDirtyMenu();
        }

        if (activeTabId === 4) {
            return hasDirtySeo();
        }

        return false;
    }, [getActiveTabId, hasDirtyMenu, hasDirtySeo, hasDirtySettings]);

    const shouldWarnForTabChange = useCallback((nextTabId: number) => {
        const activeTabId = getActiveTabId();
        if (activeTabId === nextTabId) {
            return false;
        }

        if (activeTabId === 1) {
            return hasDirtySettings();
        }

        if (activeTabId === 3) {
            return hasDirtyMenu();
        }

        if (activeTabId === 4) {
            return hasDirtySeo();
        }

        return false;
    }, [getActiveTabId, hasDirtyMenu, hasDirtySeo, hasDirtySettings]);

    const activateTab = useCallback((id: number) => {
        applyTabChange(id);

        if (id === 1) {
            refetch();
            return;
        }

        if (id === 2) {
            setPagesRefreshToken((prev) => prev + 1);
            return;
        }

        if (id === 3) {
            setMenuRefreshToken((prev) => prev + 1);
        }
    }, [applyTabChange, refetch]);

    const handleChangeTabs = (id: number) => {
        if (shouldWarnForTabChange(id)) {
            pendingTabIdRef.current = id;
            setShowWarningPopup(true);
            return;
        }

        activateTab(id);
    };

    useEffect(() => {
        if (hasUnsavedChanges()) {
            navigationConfirmedRef.current = false;
        }
    }, [hasUnsavedChanges, metaTags, description, settingConfiguration, tabs]);

    const useNavigationBlocker = () => {
        const { navigator } = useContext(NavigationContext);

        useEffect(() => {
            const push = navigator.push;
            navigator.push = (...args: Parameters<typeof push>) => {
                const [to, state, options] = args;

                if (navigationConfirmedRef.current || !hasUnsavedChanges()) {
                    push(to, state, options);
                    return;
                }

                setShowWarningPopup(true);
                pendingNavigationActionRef.current = () => {
                    navigationConfirmedRef.current = true;
                    push(to, state, options);
                };

                return;
            };

            return () => {
                navigator.push = push;
            };
        }, [hasUnsavedChanges, navigator]);
    };

    useNavigationBlocker();

    useEffect(() => {
        const container = tabsContainerRef.current;
        if (!container) return;

        // Create ResizeObserver
        const resizeObserver = new ResizeObserver((entries) => {
            for (let entry of entries) {
                const width = entry.contentRect.width;
                if (width <= 1350) {
                    setIsResponsive(true);
                } else {
                    setIsResponsive(false);
                }
            }
        });

        // Observe the container
        resizeObserver.observe(container);

        // Cleanup
        return () => {
            resizeObserver.unobserve(container);
            resizeObserver.disconnect();
        };
    }, []);


    useEffect(() => {
        if (!company?.website) return;

        const nextSettingConfiguration = {
            websiteName: company.website.name || "",
            websiteURl: company.website.webDomain || "",
            logo: company.website.settings?.logo?.url || "",
            primaryColor: company.website.settings?.metaData?.colorSettings?.primaryColor ?? '#0050FF',
            secondaryColor: company.website.settings?.metaData?.colorSettings?.secondaryColor ?? '#F59E0B',
            textColor: company.website.settings?.metaData?.colorSettings?.textColor ?? '#003366',
            fontFamily: company.website.settings?.metaData?.font?.primaryFont ?? "Roboto",
            logoPreview: company.website.settings?.logoSignedUrl || "",
            timezone: company.website.timeZone ?? "",
            loginDescription: company.website.description ?? "",
            loginHeading: company.website.welcomeNote ?? "",
            favicon: company.website.settings?.favicon?.url || "",
            faviconPreview: company.website.settings?.faviconSignedUrl,
            termOfService: company.website.settings?.metaData?.legalConsentSettings?.termOfService ?? "",
            privacyPolicy: company.website.settings?.metaData?.legalConsentSettings?.privacyPolicy ?? "",
            cookieEnabled: company.website.settings?.metaData?.cookiesConsentBanner?.enabled ?? false,
            headerHtml: company.website.settings?.metaData?.customCodeSetting?.headerHtml ?? "",
            customCss: company.website.settings?.metaData?.customCodeSetting?.customCss ?? "",
            customJs: company.website.settings?.metaData?.customCodeSetting?.customJs ?? "",
            fileName: company.website.settings?.favicon?.fileName ?? "",
            fileSize: company.website.settings?.favicon?.size ?? 0,
            logoFileName: company.website.settings?.logo?.fileName ?? "",
            logoFileSize: company.website.settings?.logo?.size ?? 0,
            faviconFileName: company.website.settings?.favicon?.fileName ?? "",
            faviconFileSize: company.website.settings?.favicon?.size ?? 0,
            statusId: company.website.statusId

        };
        const nextMetaTags = company.website.seoSettings?.metaTags || [];
        const nextDescription = company.website.seoSettings?.metaDescription || '';

        setSettingConfiguration(nextSettingConfiguration);
        setMetaTags(nextMetaTags);
        setDescription(nextDescription);
        setNewMetaTag('');
        initialSettingSnapshotRef.current = serializeState(nextSettingConfiguration);
        initialSeoSnapshotRef.current = serializeState({ metaTags: nextMetaTags, description: nextDescription });
        initialSeoStateRef.current = {
            metaTags: nextMetaTags,
            description: nextDescription,
            newMetaTag: '',
        };
    }, [company, serializeState]);


    const handleSubmit = async () => {
        const payload = {
            name: settingConfiguration?.websiteName,
            webDomain: settingConfiguration?.websiteURl,
            timeZone: settingConfiguration?.timezone,
            welcomeNote: settingConfiguration?.loginHeading,
            description: settingConfiguration?.loginDescription,
            settings: {
                logo: settingConfiguration?.logo && settingConfiguration?.logoFileName ? {
                    url: settingConfiguration?.logo,
                    fileName: settingConfiguration?.logoFileName,
                    ...(settingConfiguration?.logoFileSize && {
                        size: String(settingConfiguration?.logoFileSize)
                    })
                } : null,
                favicon: settingConfiguration?.favicon && settingConfiguration?.faviconFileName ? {
                    url: settingConfiguration?.favicon,
                    fileName: settingConfiguration?.faviconFileName,
                    ...(settingConfiguration?.faviconFileSize && {
                        size: String(settingConfiguration?.faviconFileSize)
                    })
                } : null,
                metaData: {
                    colorSettings: {
                        primaryColor: settingConfiguration?.primaryColor,
                        secondaryColor: settingConfiguration?.secondaryColor,
                        textColor: settingConfiguration?.textColor
                    },
                    font: {
                        primaryFont: settingConfiguration?.fontFamily
                    },
                    legalConsentSettings: {
                        termOfService: settingConfiguration?.termOfService,
                        privacyPolicy: settingConfiguration?.privacyPolicy
                    },
                    cookiesConsentBanner: {
                        enabled: settingConfiguration?.cookieEnabled
                    },
                    customCodeSetting: {
                        headerHtml: settingConfiguration?.headerHtml,
                        customCss: settingConfiguration?.customCss,
                        customJs: settingConfiguration?.customJs
                    }
                }
            }
        };

        try {
            let response = await updateSettingDetails(payload).unwrap();
            initialSettingSnapshotRef.current = serializeState(settingConfiguration);
            refetch()
            showToast('Success', response?.message, 'success');
        } catch (error: any) {
            // ✅ centralized error handling
            const message =
                error?.data?.message ||
                error?.error ||
                'Something went wrong while updating website';
            showToast('Error', message, 'error');
        }
    };




    const handleSeoSettings = async () => {

        let payload = {
            seoSettings: {
                metaTags: metaTags,
                metaDescription: description
            },
        }
        try {
            let response = await updateSettingDetails(payload).unwrap();
            initialSeoSnapshotRef.current = serializeState({ metaTags, description });
            initialSeoStateRef.current = {
                metaTags,
                description,
                newMetaTag: '',
            };
            setNewMetaTag('');
            refetch();
            showToast('Success', response?.message, 'success');
        } catch (error: any) {
            const message =
                error?.data?.message ||
                error?.error ||
                'Something went wrong while creating website';
            showToast('Error', message, 'error');
        }

    }




    return (
        <div ref={tabsContainerRef}>
            <Container theme={theme} className='rounded-0'>
                <div className="d-flex justify-content-between mt-0">
                    <div className="d-flex align-items-center gap-2 justify-content-between w-100">
                        <TitleGroup>
                            <TitleWrapper>
                                <Title theme={theme}>Website Builder</Title>
                            </TitleWrapper>
                            <StyledText className='mt-2' fontSize="14px" fontWeight={300} textColor={theme.CorporateTheme.colors.textSecondary}>
                                Create stunning website within minutes.
                            </StyledText>
                        </TitleGroup>
                        {tabs[0].active ? <Button
                            theme={theme}
                            btnBg={theme?.CorporateTheme?.colors?.primary}
                            color={theme?.CorporateTheme?.colors?.background}
                            type="button"
                            $variant="customButton"
                            borderColor='none'
                            className={`px-3`}
                            onClick={handleSubmit}
                            disabled={isUpdating}

                        >
                            {isUpdating ? "Saving..." : 'Save Changes'}
                        </Button> : tabs[2].active ?
                            <><Button
                                theme={theme}
                                btnBg={theme?.CorporateTheme?.colors?.primary}
                                color={theme?.CorporateTheme?.colors?.background}
                                type="button"
                                $variant="customButton"
                                borderColor='none'
                                className={`px-3`}
                                disabled={isCreating}
                                onClick={() => submitRef?.current?.handleSubmit()}

                            >
                                {isCreating ? "Saving..." : "Save menu settings"}
                            </Button>
                                <Button
                                    theme={theme}
                                    btnBg={theme?.CorporateTheme?.colors?.filterBG}
                                    color={theme?.CorporateTheme?.colors?.textSecondary}
                                    type="button"
                                    $variant="customButton"
                                    className="px-3"
                                    disabled={isCreating}
                                    onClick={() => submitRef?.current?.handleCancel()}
                                >
                                    Cancel
                                </Button>

                            </> : tabs[3].active ? <Button
                                theme={theme}
                                btnBg={theme?.CorporateTheme?.colors?.primary}
                                color={theme?.CorporateTheme?.colors?.background}
                                type="button"
                                $variant="customButton"
                                borderColor='none'
                                className={`px-3`}
                                disabled={isUpdating}
                                onClick={handleSeoSettings}

                            >
                                {isUpdating ? "Saving..." : "Save Seo Settings"}
                            </Button> : null}
                    </div>
                </div>
                <div className='mt-3'>
                    <Tabs tabs={tabs} onChangeTabs={handleChangeTabs} inactiveColor={theme.CorporateTheme.colors.headerColor} inactiveFontWeight={500} />
                </div>
            </Container>
            <div className='container-fluid pt-3 pb-3 overflow-auto' style={{ height: 'calc(100vh - 178px)', backgroundColor: theme.CorporateTheme.colors.bannerBg }}>
                {tabs[0].active ? <Setting companyLoading={companyLoading} isResponsive={isResponsive} setSettingConfiguration={setSettingConfiguration} settingConfiguration={settingConfiguration} createdAt={company?.website?.createdAt} updateAt={company?.website?.updatedAt} totalPages={company?.publishedPagesCount ?? 0} customWebDomain={company?.website?.customWebDomain} /> : null}
                {tabs[1].active ? <Pages key={`pages-${pagesRefreshToken}`} domain={company?.website?.cloudflareWebDomain} companyLoading={companyLoading} isResponsive={isResponsive} refreshToken={pagesRefreshToken} /> : null}
                {tabs[2].active ? <MenuBuilder key={`menu-${menuRefreshToken}`} ref={submitRef} createMenu={createMenu} refreshToken={menuRefreshToken} /> : null}
                {tabs[3].active ? <SeoSetting companyLoading={companyLoading} metaTags={metaTags} setMetaTags={setMetaTags} description={description} setDescription={setDescription} newMetaTag={newMetaTag} setNewMetaTag={setNewMetaTag} /> : null}
                {tabs[4].active ? <DomainSettings customWebDomain={company?.website?.customWebDomain} cloudflareWebDomain={company?.website?.cloudflareWebDomain} /> : null}

            </div>

            {showWarningPopup && (
                <ConfirmationPrompt
                    title="Cancel Editing?"
                    subTitle="You might have unsaved changes. Are you sure you want to leave without saving?"
                    onConfirm={() => {
                        setShowWarningPopup(false);
                        if (pendingTabIdRef.current !== null) {
                            activateTab(pendingTabIdRef.current);
                            pendingTabIdRef.current = null;
                            return;
                        }

                        if (pendingNavigationActionRef.current) {
                            pendingNavigationActionRef.current();
                            pendingNavigationActionRef.current = null;
                        }
                    }}
                    onCancel={() => {
                        pendingTabIdRef.current = null;
                        pendingNavigationActionRef.current = null;
                        setShowWarningPopup(false);
                    }}
                />
            )}
        </div>
    )
}

export default Builder
